import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import ErrorMessage from '@/components/common/ErrorMessage';
import EyeCloseIcon from '@/components/Icons/EyeCloseIcon';
import EyeOpenIcon from '@/components/Icons/EyeOpenIcons';
import PasswordIcon from '@/components/Icons/PasswordIcons';
import CardLayout from '@/components/Layout/CardLayout';
import FieldLayout from '@/components/Layout/FieldLayout';
import Button from '@/components/ui/Button';
import Label from '@/components/ui/Label';
import PrimaryInput from '@/components/ui/PrimaryInput';
import SearchSelectBox, {
  ISelectOption,
} from '@/components/ui/SearchSelectBox';
import { IAddDeviceForm } from '@/interfaces/biometricDevice.model';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Modal } from 'reactstrap';
import { IOfficeResponse } from '../HrmSettings/OfficeLocation/OfficeLocation';
import { IDeleteResponse } from '@/interfaces/common.model';
interface IAddBiometricDeviceProps {
  isOpen: boolean;
  onClose: () => void;
  editData: IAddDeviceForm | null;
  getDevices: () => Promise<void>;
}
const AddBiometricDevice = ({
  isOpen,
  onClose,
  editData,
  getDevices,
}: IAddBiometricDeviceProps) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    control,
    formState: { errors },
  } = useForm<IAddDeviceForm>();
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  useEffect(() => {
    if (editData) {
      reset({
        name: editData?.name,
        ip: editData?.ip,
        password: editData?.password,
        officeId: editData?.officeId,
      });
    }
  }, [editData]);
  const getOffices = async (inputValue?: string): Promise<ISelectOption[]> => {
    const filter = {
      search: inputValue,
      pageNumber: 1,
      pageSize: 50,
      type: 'office',
    };

    const api = `${API_URL}${API_ENDPOINT.getSite}?${getFilterUrl(filter)}`;

    try {
      const response: IOfficeResponse = await callApi(api, {
        method: 'GET',
      });

      if (response.status && response.data?.site?.length) {
        return response.data.site.map((office) => ({
          value: office.id,
          label: office.name,
        }));
      }

      return [];
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }

      return [];
    }
  };
  const onSubmit: SubmitHandler<IAddDeviceForm> = async (data) => {
    setIsLoading(true);
    const postData = {
      name: data?.name,
      address: data?.ip,
      password: data?.password,
      siteId: data?.officeId,
    };
    try {
      const response: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.addDevice,
        {
          method: 'POST',
          data: postData,
        },
      );
      if (response?.status) {
        showToastNotification('success', response?.message);
        getDevices();
        reset();
        onClose();
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Modal
      className="modal-dialog modal-dialog-centered modal-md"
      isOpen={isOpen}
    >
      <div className="modal-content shadow-lg">
        <div className="modal-header p-4">
          <h1
            className="modal-title fs-3 rajdhani-semibold"
            id="addHolidayModalLabel"
          >
            Add Devices
          </h1>
          <button
            type="button"
            className="btn-close"
            onClick={() => {
              clearErrors();
              onClose();
              reset();
            }}
          />
        </div>
        <div className="modal-body p-4">
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardLayout cardStyle="shadow-none mb-0">
              <div className="row">
                <div className="col-12">
                  <div className="form-group mb-3">
                    <PrimaryInput
                      label="Name"
                      type="text"
                      id="name"
                      placeholder="Enter Name"
                      required={true}
                      {...register('name', {
                        ...errorTypes.required(),
                        ...errorTypes.name,
                      })}
                      errors={errors?.name?.message}
                    />
                  </div>
                </div>

                <FieldLayout>
                  <Label required={true} id="officeId">
                    Office
                  </Label>
                  <Controller
                    control={control}
                    name="officeId"
                    rules={errorTypes.required('officeId')}
                    render={({ field }) => (
                      <SearchSelectBox
                        value={field.value}
                        title="Select Office"
                        onChange={(option) => {
                          field.onChange(option?.value || '');
                        }}
                        isAsync
                        fetchOptions={getOffices}
                        defaultFetch
                        disable={isLoading}
                        isUsingFilter
                      />
                    )}
                  />
                  {/* <label htmlFor="Role" className="form-label">
            Office
            <span className="text-danger">*</span>
          </label>
          <select
            className="form-select form-control"
            aria-label="Default select example"
            {...register('officeId', {
              ...errorTypes.required('Office'),
            })}
          >
            <option value="" className="d-none">
              Select office
            </option>
            {officeList?.length > 0 &&
              officeList?.map((item, index) => {
                return (
                  <option key={index} value={item?.id}>
                    {item?.name}
                  </option>
                );
              })}
          </select> */}
                  <ErrorMessage message={errors?.officeId?.message} />
                </FieldLayout>
                {/* <div className="col-12">
                  <div className="form-group mb-3">
                    <Label id="deviceType" required={true}>
                      Device Type
                    </Label>
                  </div>
                </div> */}
                <div className="col-12">
                  <div className="form-group mb-3">
                    <PrimaryInput
                      label="Machine IP"
                      type="text"
                      id="ip"
                      placeholder="Enter machine ip"
                      required={true}
                      {...register('ip', {
                        ...errorTypes.required(),
                      })}
                      errors={errors?.ip?.message}
                    />
                    {/* <PrimaryInput /> */}
                  </div>
                  <div className="col-12 auth">
                    <div className="form-group mb-3">
                      <PrimaryInput
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={'Enter password'}
                        {...register('password', {
                          required: 'Please enter a valid password',
                        })}
                        errors={errors?.password?.message}
                        startIcon={<PasswordIcon />}
                        passwordShow={
                          <div
                            className="password-show"
                            onClick={togglePasswordVisibility}
                            style={{ cursor: 'pointer' }}
                          >
                            <span
                              // type="button"
                              className=""
                              style={{ fontSize: '14px', cursor: 'pointer' }}
                            >
                              {showPassword ? (
                                <EyeOpenIcon />
                              ) : (
                                <EyeCloseIcon />
                              )}
                            </span>
                          </div>
                        }
                      />
                    </div>
                  </div>
                  {/* <div className="col-12">
                    <div className="form-group mb-3">
                      <Label required={true} id="deviceDirection">
                        Device Direction
                      </Label>
                      <select
                        className={`form-select fw-normal status-dropdown rounded-4 px-3`}
                        {...register('deviceDirection')}
                      >
                        <option>tt</option>
                      </select>
                    </div>
                  </div> */}
                </div>
              </div>
            </CardLayout>
            <div className="modal-footer p-2">
              <Button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  clearErrors();
                  onClose();
                  reset();
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn-primary"
                isLoading={isLoading}
              >
                {editData ? 'Edit Device' : 'Add Device'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddBiometricDevice;
